import { createContext, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { get, ApiError } from "./api";
import type { Me } from "./api";

// Who is signed in, resolved once at start-up.
//
// `null` means definitively signed out; `undefined` means we have not asked
// yet. Collapsing the two would flash the login screen at somebody who is
// already signed in, on every page load.
interface AuthState {
  me: Me | null | undefined;
  reload: () => Promise<void>;
}

const Ctx = createContext<AuthState>({ me: undefined, reload: async () => {} });

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null | undefined>(undefined);

  const reload = async () => {
    try {
      setMe(await get<Me>("/api/me"));
    } catch (e) {
      // A 401 is the normal signed-out answer, not a failure worth showing.
      if (e instanceof ApiError && e.status === 401) setMe(null);
      else throw e;
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  return <Ctx.Provider value={{ me, reload }}>{children}</Ctx.Provider>;
}

export const useAuth = () => useContext(Ctx);
